/**
 * @fileoverview Visualization overlays drawn on top of the building.
 *
 * - deviation:   flow vs design flow (the balancing workhorse)
 * - temperature: room temperature vs setpoint
 * - return:      per-room return temperature (heat-pump health)
 * - heatflux:    per-room heat balance (radiator output − losses)
 *
 * @module HydraulicBalancingTesting/components/Overlays
 */

import React from 'react';

import { SVG_DIM, COLORS } from '../constants';
import { toLitersPerHour } from '../physics';
import { deviationColor } from './RoomUnit';

const { ROOM_WIDTH, ROOM_HEIGHT, MARGIN_LEFT, GROUND_Y, WIDTH } = SVG_DIM;

/** Overlay caption position: top of the canvas, centred to the right of the outdoor block. */
const CAPTION_X = (150 + WIDTH) / 2;
const CAPTION_Y = 40;

const roomRect = (room) => ({
  x: MARGIN_LEFT + ROOM_WIDTH * room.col,
  y: GROUND_Y - ROOM_HEIGHT * (room.floor + 1),
});

const Cell = ({ room, fill, children }) => {
  const { x, y } = roomRect(room);
  return (
    <g pointerEvents="none">
      <rect x={x + 2} y={y + 2} width={ROOM_WIDTH - 4} height={ROOM_HEIGHT - 4} fill={fill} />
      <g transform={`translate(${x + ROOM_WIDTH / 2}, ${y + ROOM_HEIGHT / 2})`}>{children}</g>
    </g>
  );
};

// ----------------------------------------------------------------------------
// Flow deviation overlay
// ----------------------------------------------------------------------------

export const DeviationOverlay = ({ rooms, deviations, roomFlows }) => (
  <g>
    {rooms.map((room, idx) => {
      const dev = deviations[idx];
      const color = deviationColor(dev);
      return (
        <Cell key={room.id} room={room} fill={`${color}33`}>
          <text textAnchor="middle" y={-8} fontSize={24} fontWeight={700} fill={color}>
            {dev >= 0 ? '+' : ''}{(dev * 100).toFixed(0)}%
          </text>
          <text textAnchor="middle" y={14} fontSize={11} fill={COLORS.GRAY}>
            {toLitersPerHour(roomFlows[idx]).toFixed(0)} / {toLitersPerHour(room.designFlow).toFixed(0)} l/h
          </text>
          <text textAnchor="middle" y={30} fontSize={10} fill={color} fontWeight={600}>
            {Math.abs(dev) <= 0.1 ? 'balanced' : dev > 0 ? 'overflow' : 'starving'}
          </text>
        </Cell>
      );
    })}
    <text x={CAPTION_X} y={CAPTION_Y} textAnchor="middle" fontSize={12} fill={COLORS.GRAY}>
      Flow deviation vs design flow (commissioning conditions, TRVs open)
    </text>
  </g>
);

// ----------------------------------------------------------------------------
// Temperature overlay
// ----------------------------------------------------------------------------

const tempFill = (T, Tset) => {
  const err = Math.max(-3, Math.min(3, T - Tset));
  if (err < 0) {
    const a = Math.min(Math.abs(err) / 3, 1) * 0.35;
    return `rgba(24, 144, 255, ${a})`;
  }
  const a = Math.min(err / 3, 1) * 0.35;
  return `rgba(255, 77, 79, ${a})`;
};

export const TemperatureOverlay = ({ rooms }) => (
  <g>
    {rooms.map((room) => {
      const err = room.T - room.Tset;
      const color = Math.abs(err) <= 0.5 ? COLORS.GOOD : err > 0 ? COLORS.HOT : COLORS.COLD;
      return (
        <Cell key={room.id} room={room} fill={tempFill(room.T, room.Tset)}>
          <text textAnchor="middle" y={-6} fontSize={24} fontWeight={700} fill={color}>
            {room.T.toFixed(1)}°C
          </text>
          <text textAnchor="middle" y={16} fontSize={12} fill={COLORS.GRAY}>
            {err >= 0 ? '+' : ''}{err.toFixed(1)} °C vs setpoint
          </text>
        </Cell>
      );
    })}
  </g>
);

// ----------------------------------------------------------------------------
// Return temperature overlay
// ----------------------------------------------------------------------------

const returnTint = (tRet) => {
  const t = Math.max(0, Math.min(1, (tRet - 30) / 14));
  return `rgba(${Math.round(82 + t * 173)}, ${Math.round(196 - t * 119)}, ${Math.round(26 + t * 53)}, 0.22)`;
};

export const ReturnTempOverlay = ({ rooms, tSupply }) => (
  <g>
    {rooms.map((room) => {
      const spread = tSupply - room.Treturn;
      const good = spread >= 4;
      return (
        <Cell key={room.id} room={room} fill={returnTint(room.Treturn)}>
          <text textAnchor="middle" y={-10} fontSize={16} fontWeight={700} fill={good ? COLORS.GOOD : COLORS.WARM}>
            {room.Treturn.toFixed(1)}°C
          </text>
          <text textAnchor="middle" y={3} fontSize={9} fill={COLORS.GRAY}>
            return temp
          </text>
          <text textAnchor="middle" y={22} fontSize={12} fontWeight={600} fill={good ? COLORS.GOOD : COLORS.WARM}>
            Δ {spread.toFixed(1)} °C {good ? '✓' : '⚠ low'}
          </text>
          <text textAnchor="middle" y={35} fontSize={9} fill={COLORS.GRAY}>
            supply − return spread
          </text>
        </Cell>
      );
    })}
    <text x={CAPTION_X} y={CAPTION_Y} textAnchor="middle" fontSize={12} fill={COLORS.GRAY}>
      Per-room return temperature — high returns hurt the heat-pump COP
    </text>
  </g>
);

// ----------------------------------------------------------------------------
// Heat-loss overlay (per-room heat balance, school-style calculation)
// ----------------------------------------------------------------------------

/** Subtle tint by net heat balance: warming = red, cooling = blue, steady = green. */
const netFill = (net) =>
  net > 20 ? 'rgba(255, 77, 79, 0.12)' : net < -20 ? 'rgba(24, 144, 255, 0.12)' : 'rgba(82, 196, 26, 0.12)';

export const HeatFluxOverlay = ({ rooms }) => (
  <g>
    {rooms.map((room) => {
      if (!room.losses) return null;
      const loss = Math.max(room.losses.total, 0);
      const output = room.qrad;
      const net = output - loss;
      const netColor = net > 20 ? COLORS.HOT : net < -20 ? COLORS.COLD : COLORS.GOOD;
      const labelX = -62;
      const numX = 60;
      return (
        <Cell key={room.id} room={room} fill={netFill(net)}>
          {/* white plate so the calculation stays readable over pipes/labels */}
          <rect x={-66} y={-30} width={132} height={70} rx={6} fill="white" opacity={0.95} stroke={COLORS.BORDER} />
          {/* radiator output (gain, +) */}
          <text x={labelX} y={-16} fontSize={10} fill={COLORS.GRAY}>radiator</text>
          <text x={numX} y={-16} fontSize={14} textAnchor="end" fill={COLORS.HOT}>
            + {output.toFixed(0)} W
          </text>
          {/* heat loss (−) */}
          <text x={labelX} y={4} fontSize={10} fill={COLORS.GRAY}>heat loss</text>
          <text x={numX} y={4} fontSize={14} textAnchor="end" fill={COLORS.WARM}>
            − {loss.toFixed(0)} W
          </text>
          {/* rule */}
          <line x1={labelX} y1={12} x2={numX} y2={12} stroke={COLORS.GRAY} strokeWidth={1.25} />
          {/* net total */}
          <text x={labelX} y={31} fontSize={10} fill={COLORS.GRAY}>{'net'}</text>
          <text x={numX} y={32} fontSize={16} fontWeight={700} textAnchor="end" fill={netColor}>
            = {net >= 0 ? '+' : '−'} {Math.abs(net).toFixed(0)} W
          </text>
        </Cell>
      );
    })}
    <text x={CAPTION_X} y={CAPTION_Y} textAnchor="middle" fontSize={12} fill={COLORS.GRAY}>
      Per-room heat balance — radiator output minus current losses
    </text>
  </g>
);
